import { useAuth } from "@flaminem/authentication";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useGetTenantsByIdsQuery } from "src/core/api/tenant";
import { getAvailableTenantsFromToken, getTenantIdFromToken } from "src/core/authentication/jwt";
import { TENANT_STATUS, TenantOption, TenantStatus } from "src/core/types/tenant";

export type TenantContextValue = {
  /**
   * Tenant sur lequel l'utilisateur est connecté (courant).
   * IMPORTANT: l'URL reste la source de vérité, le guard appelle switchTenant().
   */
  tenantId: string | null;

  /**
   * Liste des tenants disponibles VALIDÉS par tenantService.
   */
  availableTenants: TenantOption[];

  /**
   * Statut du tenant.
   */
  status: TenantStatus;

  /**
   * Statut “technique” : validation des droits via tenantService.
   * (utile pour RootRedirect / Guard)
   */
  isValidatingTenants: boolean;

  /**
   * Tenant par défaut issu du token (main tenant).
   * Peut être null si non présent.
   * ATTENTION: il n'est appliqué que s'il est autorisé par tenantService.
   */
  defaultTenantIdFromToken: string | null;

  /**
   * Change le tenant courant.
   * IMPORTANT: ne purge plus Apollo ici (client recréé via TenantApolloProvider tenant-aware).
   */
  switchTenant: (newTenantId: string) => Promise<void>;
};

export const TenantContext = createContext<TenantContextValue | null>(null);

export const useTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used within TenantProvider");
  }
  return ctx;
};

/**
 * TenantProvider :
 * Rôle fonctionnel :
 * - Centraliser la gestion du tenant courant de l'utilisateur.
 * - Valider les tenants accessibles via le tenantService (à partir du token).
 * - Exposer l'état tenant (INITIALIZING / VALIDATING / SWITCHING / READY).
 *
 * Principes clés :
 * - L'URL est la SOURCE DE VÉRITÉ pour le tenant courant.
 *   → Les guards (TenantParamGuard / RootRedirect) appellent switchTenant().
 * - Le provider ne choisit jamais un tenant tout seul au boot.
 */
export const TenantProvider = ({ children }: PropsWithChildren) => {
  const auth = useAuth();

  const token = auth.user?.accessToken ?? null;

  const tokenTenantIds = useMemo(() => getAvailableTenantsFromToken(token), [token]);
  const defaultTenantIdFromToken = useMemo(() => getTenantIdFromToken(token), [token]);

  /**
   * URL = source de vérité :
   * le tenant est fixé par les guards de routing (TenantParamGuard / RootRedirect)
   * via l'appel à switchTenant().
   *
   * => On ne déduit JAMAIS le tenant automatiquement ici au boot,
   * sauf via une redirection explicite.
   */
  const [tenantId, setTenantId] = useState<string | null>(null);

  const [status, setStatus] = useState<TenantStatus>(TENANT_STATUS.INITIALIZING);

  // Anti-race condition (switch A->B->C)
  const switchSeqRef = useRef(0);

  /**
   * Validation via tenantService :
   * - On envoie la liste des tenantIds issue du token JWT
   * - Le tenantService renvoie UNIQUEMENT les tenants réellement accessibles
   *
   * IMPORTANT :
   * - Le token seul n'est PAS suffisant pour autoriser l'accès.
   * - Le backend reste la source de vérité des droits.
   */
  const mustValidateTenants = auth.isAuthenticated && tokenTenantIds.length > 0;

  const {
    data: tenantServiceTenants,
    isFetching: isFetchingTenants,
    isLoading: isLoadingTenants,
    isUninitialized: isTenantsUninitialized,
  } = useGetTenantsByIdsQuery(
    { tenantIds: tokenTenantIds },
    {
      // Pas d'appel si pas authentifié ou pas de tenantIds
      skip: !mustValidateTenants,
    }
  );

  /**
   * Mapping des tenants réellement accessibles (vide tant que validation pas terminée).
   * - Si token sans tenants => liste vide
   * - Si tenantService renvoie vide => liste vide
   * - Sinon on mappe les tenants réellement accessibles.
   */
  const availableTenants: TenantOption[] = useMemo(() => {
    if (!auth.isAuthenticated) return [];
    if (!tenantServiceTenants) return [];
    return tenantServiceTenants.map((t) => ({
      id: t.tenantId,
      name: t.tenantName,
    }));
  }, [auth.isAuthenticated, tenantServiceTenants]);

  const isValidatingTenants =
    auth.isAuthenticated &&
    mustValidateTenants &&
    (isTenantsUninitialized || isLoadingTenants || isFetchingTenants);

  /**
   * switchTenant :
   * Change le tenant courant.
   *
   * Points importants :
   * - Appelé UNIQUEMENT par les guards de routing (URL → contexte).
   * - Protégé contre les race conditions (A → B → C).
   * - Ne purge plus Apollo ici :
   *   le client est recréé dans TenantApolloProvider (tenant-aware).
   *
   * UX :
   * - Passe par l'état SWITCHING pour permettre un overlay
   *   sans démonter toute l'application.
   */
  const switchTenant = useCallback(
    async (newTenantId: string) => {
      if (newTenantId === tenantId) return;

      const seq = ++switchSeqRef.current;

      setStatus(TENANT_STATUS.SWITCHING);
      setTenantId(newTenantId);

      // Laisse React appliquer l'état avant de repasser READY (utile overlay UX)
      await Promise.resolve();

      if (seq === switchSeqRef.current) {
        setStatus(TENANT_STATUS.READY);
      }
    },
    [tenantId]
  );

  /**
   * Auth -> reset
   */
  useEffect(() => {
    if (!auth.isAuthenticated) {
      switchSeqRef.current += 1; // annule un switch en cours
      setTenantId(null);
      setStatus(TENANT_STATUS.INITIALIZING);
    }
  }, [auth.isAuthenticated]);

  /**
   * Mise à jour du status global du tenant.
   *
   * IMPORTANT :
   * - Ne JAMAIS écraser SWITCHING ici.
   * - SWITCHING est piloté uniquement par switchTenant().
   *
   * États possibles :
   * - INITIALIZING : aucun tenant encore résolu
   * - VALIDATING   : appel tenantService en cours
   * - SWITCHING    : changement de tenant demandé
   * - READY        : tenant résolu et stable
   */
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    setStatus((prev) => {
      if (prev === TENANT_STATUS.SWITCHING) return prev;

      if (isValidatingTenants) return TENANT_STATUS.VALIDATING;

      return tenantId ? TENANT_STATUS.READY : TENANT_STATUS.INITIALIZING;
    });
  }, [auth.isAuthenticated, isValidatingTenants, tenantId]);

  const value: TenantContextValue = useMemo(
    () => ({
      tenantId,
      availableTenants,
      status,
      isValidatingTenants,
      defaultTenantIdFromToken,
      switchTenant,
    }),
    [
      tenantId,
      availableTenants,
      status,
      isValidatingTenants,
      defaultTenantIdFromToken,
      switchTenant,
    ]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
